package terraultima

import kotlin.math.*
import kotlin.random.Random

// =====================================================================================
//  PHYSICS  — source 2 of D001. Deterministic given state; this is where death lives.
//  Rule (D001): pressure-driven content may never kill. Arithmetic kills. That is what
//  makes a loss feel fair, and it is the entire reason this layer exists separately.
// =====================================================================================
object Physics {

    // Grounded in the closed-loop agriculture literature: a full human diet needs roughly
    // 40-50 m^2 of intensive growing area, so ~0.022 person-months of food per m^2 per month.
    const val YIELD_PER_M2 = 0.022
    const val LS_KW_PER_PERSON = 1.2       // ECLSS + habitat, per awake person

    /**
     * What one awake person drinks, washes and grows with in a month, in the units the water stock
     * is counted in. It is the threshold health already uses (`water > awake * 0.5` is "watered"),
     * named so the interface can divide the reserve by it and say how long the reserve IS.
     */
    const val WATER_PER_PERSON_MONTH = 0.5
    const val CRYO_KW_PER_POD = 0.05
    // R2-grounded (docs/research/R2-realism-grounding.md): full dietary closure under artificial
    // light costs 10-25 kWe PER PERSON. At ~45 m^2/person that is ~0.22 kW/m^2. This single number
    // is why a colony needs a real power plant, and it makes power — not food — the master
    // constraint. The first version of this model was 4x too generous and made everything survivable.
    const val GROW_KW_PER_M2 = 0.20
    /** Value of the labour product at neutral gauges — used to normalise the index to ~1.0. */
    const val LABOUR_NEUTRAL = 0.617
    /** Two-year half-life on a power commitment: 0.5^(1/24) per month. See D008. */
    const val POWER_COMMIT_DECAY = 0.9715
    /**
     * A propellant plant makes about one ascent load (~2 km/s for a small lander) in three years of
     * running. Slow enough that "there is fuel in the tank" is a settled-colony fact, not a
     * landfall one, and visible on the stores line the whole time it is filling.
     */
    const val ISRU_KMS_PER_MONTH = 0.056
    const val ISRU_TANK_CAP = 6.0

    /**
     * The outcomes an option can resolve to for this colony: the gated branches whose gates hold,
     * or, when none does, the ungated ones. The engine draws from this list and the screen prices
     * it, so what the player is shown and what can happen are the same list.
     */
    fun liveOutcomes(s: WorldState, o: Option, caps: Set<String>): List<Outcome> =
        o.outcomes.filter { it.requires.isNotEmpty() && it.requires.all { r -> Expr.cond(s, caps, r) } }
            .ifEmpty { o.outcomes.filter { it.requires.isEmpty() } }

    fun capabilities(s: WorldState): Set<String> {
        val out = mutableSetOf<String>()
        val pr = powerRatio(s)
        for (m in s.installed) {
            val mod = Content.module(m) ?: continue
            if ((s.moduleHealth[m] ?: 0.0) <= 0.25) continue
            if (!mod.phase.contains("ANY") && !mod.phase.contains(s.phase.name)) continue
            // A brownout takes capabilities away. Losing an option you relied on for 20 years
            // is free drama, and it is honest: power is upstream of everything.
            if (mod.powerDraw > 0.5 && pr < 0.75) continue
            out.addAll(mod.grants)
        }
        if (s.popTotal >= 4) out.add("MANPOWER")
        if (s.gauge["competence"]!! > 45) out.add("EXPERTISE")
        // Institutions grant verbs too. An elected council really does let the colony decide
        // formally; there is no crate of hardware involved, and pretending there is was why a
        // player who had just granted a council still read "LOCKED — needs vote".
        out.addAll(s.granted)
        return out
    }

    /**
     * What this world is GOOD for. A hazard and an opportunity are often the same fact seen twice:
     * a shaking planet has heat under it, a world of ice is a world of water. This is where a
     * playthrough stops being "survive somewhere" and becomes "make something of *here*".
     */
    fun localAdvantages(p: Planet): List<Pair<String, String>> = buildList {
        if (p.ore >= 0.55) add("iron_economy" to
            "the ridges are shot through with ore \u2014 you could make metal here rather than ration it")
        if (p.water >= 0.65) add("deep_water" to
            "there is far more ice down there than you are using")
        if (p.seismic >= 0.5) add("geothermal" to
            "the ground that keeps shaking has heat under it")
        if (p.flora >= 0.4) add("bio_industry" to
            "things grow here already, which is more than most worlds offer")
    }

    fun modifier(s: WorldState, key: String): Double {
        var v = 0.0
        for (m in s.installed) {
            val mod = Content.module(m) ?: continue
            if ((s.moduleHealth[m] ?: 0.0) <= 0.25) continue
            v += (mod.modifiers[key] ?: 0.0) * (s.moduleHealth[m] ?: 1.0)
        }
        return v
    }

    fun powerGen(s: WorldState): Double {
        var g = 0.0
        val insol = if (s.phase == Phase.SURFACE) s.planet.insolation else spaceInsolation(s)
        for (m in s.installed) {
            val mod = Content.module(m) ?: continue
            val h = s.moduleHealth[m] ?: 0.0
            if (h <= 0.25) continue
            g += mod.powerGen * h * (if (mod.tags.contains("solar")) insol else 1.0)
        }
        // Geothermal does not care about dust storms or the long night, which is its whole appeal.
        if (s.hasFlag("geothermal")) g += 260.0 * s.planet.seismic
        return g * (1.0 - 0.25 * s.pressure["entropy"]!!)
    }

    /**
     * Solar power falls off as 1/r^2. How fast you leave the light depends on where you are going:
     * an inner-system hop ends at ~1.5 AU, an interstellar leg spends its whole cruise in the dark.
     * This is why a solar-only build cannot be an interstellar build — a real constraint, expressed
     * as arithmetic rather than as a rule forbidding the combination.
     */
    fun spaceInsolation(s: WorldState): Double {
        val rate = if (s.build.arrivalYear <= 5) 0.25 else 1.6
        val au = 1.0 + (s.month / 12.0) * rate
        return (1.0 / (au * au)).coerceIn(0.015, 1.0)
    }

    fun powerDraw(s: WorldState): Double {
        var d = 0.0
        for (m in s.installed) {
            val mod = Content.module(m) ?: continue
            val h = s.moduleHealth[m] ?: 0.0
            if (h <= 0.25) continue
            d += mod.powerDraw
        }
        d += (s.adults + s.children + s.elders) * LS_KW_PER_PERSON
        d += s.cryo * CRYO_KW_PER_POD
        d += s.cultivated * GROW_KW_PER_M2
        // What the colony has committed power TO, or freed it FROM (D008). A funded survey draws
        // power while it runs; a loop running without its polishing stage gives some back. Floored
        // at a quarter of the standing load: you cannot free more power than the colony uses.
        return max(0.25 * d, max(0.1, d - s.powerCommit))
    }

    fun powerRatio(s: WorldState): Double = powerGen(s) / powerDraw(s)

    /** People who fit in the shielded volume. 0 if the expedition carries no shelter. */
    fun shelterFor(s: WorldState): Double = modifier(s, "shelterFor")

    /**
     * EFFECTIVE LABOUR — the transmission from society to physics.
     *
     * This was missing, and its absence was the single biggest structural flaw in the simulation:
     * population only ever *consumed*. Food came from `area x yield`, maintenance from spares alone,
     * so morale, cohesion and dissent had no route to the material world at all. A colony could rot
     * socially while the lights stayed on, and a player who never engaged survived a coin flip
     * (49.5%) because the build's physics carried them.
     *
     * Now: unhappy, sick, divided or mutinous people do less work, and work is what feeds everyone
     * and keeps the machines running. This is also simply true, and it is R2's "crew-time" currency
     * that the first model dropped.
     */
    fun labourIndex(s: WorldState): Double {
        val raw = (0.60 + 0.40 * s.gauge["morale"]!! / 100.0) *
                  (0.60 + 0.40 * s.gauge["health"]!! / 100.0) *
                  (0.85 + 0.15 * s.gauge["cohesion"]!! / 100.0) *
                  (1.0 - 0.35 * s.pressure["dissent"]!!)
        // Normalised so a middling colony sits at 1.0: this is a MULTIPLIER on output, not a cap on
        // it. Expressed as a cap it created a runaway (health -> labour -> food -> health) that
        // collapsed 84% of colonies regardless of play — a positive feedback loop with no damper.
        return (raw / LABOUR_NEUTRAL).coerceIn(0.45, 1.25)
    }

    /**
     * @param notable whether these deaths are an EVENT rather than the actuarial background. Routine
     * old-age mortality was pumping `faith` every month, which is why faith reached 0.84 by y40
     * under every policy including the secular ones — an artefact masquerading as emergent religion.
     * A disaster moves faith; a long life ending does not.
     */
    fun kill(s: WorldState, n: Double, rng: Random, notable: Boolean = true) {
        var left = n
        val order = listOf("elders", "children", "adults")
        for (c in order) {
            if (left <= 0) break
            val cur = when (c) { "elders" -> s.elders; "children" -> s.children; else -> s.adults }
            val take = min(cur, left * if (c == "adults") 1.0 else 0.35)
            when (c) { "elders" -> s.elders -= take; "children" -> s.children -= take; else -> s.adults -= take }
            left -= take
        }
        s.deaths += n - left                     // `left` is what the colony was too small to lose
        if (notable) s.deathsNotable += n - left
        // Death is not just a number going down: it moves faith and morale. (Realism A1.)
        if (notable) {
            s.pressure["faith"] = (s.pressure["faith"]!! + 0.02 * n).coerceAtMost(1.0)
            s.gauge["morale"] = (s.gauge["morale"]!! - 1.5 * n).coerceIn(0.0, 100.0)
        }
    }

    /**
     * Direction of a stock over the last few years: +1 rising, -1 falling, 0 steady. Reads the same
     * yearly snapshots and the same +/-12% threshold the interface uses for "rising 3 yrs", so a
     * condition written against it is a condition the player can SEE. Returns 0 until there are
     * three years of history, which is what makes it useless as a launch-day gate — deliberately:
     * "the stores are fuller than the plan requires" cannot be true of the plan's own reserve.
     * Index is into WorldState.yearly: 0 food, 1 water, 2 spares, 3 pop, 4 powerRatio.
     */
    /**
     * YEARS UNTIL THIS STOCK IS GONE, at the rate of the last few years — or null if it is not
     * falling, or there is not enough history to say.
     *
     * A raw quantity is not information a governor can act on: "spares 507" means nothing without
     * knowing what spares are spent on. "Runs out in 9 years" is the same number expressed as the
     * thing the player actually decides about, and it is measured from the colony's own history
     * rather than from a formula, so it accounts for whatever the farm, the loop and the workshop
     * are actually doing this decade.
     *
     * Deliberately empirical: `food` used to be shown as stock-over-population ("54 months"), which
     * says the stores will be gone in 54 months even while the farm is filling them faster than the
     * colony eats. That is not a lie about arithmetic, it is a lie about the future.
     */
    fun runsOut(s: WorldState, idx: Int, now: Double): Double? {
        val h = s.yearly
        if (h.size < 3 || now <= 0.0) return null
        val span = kotlin.math.min(5, h.size - 1)
        val past = h[h.size - 1 - span][idx]
        val perYear = (past - now) / span            // positive when falling
        if (perYear <= 0.0001) return null
        return now / perYear
    }

    /**
     * What the colony MAKES in a year, and what it can hold. Both are needed by the readout, for
     * the reason iteration 2 found: `spares` read exactly 450 on all twelve late screens while
     * options charged 10, 20 and 45 of it and one paid out 50. Nothing was broken — the stock sits
     * at its storage cap and the shop refills it monthly, so a year's production restores any spend
     * before the next screen. But `runsOut` returns null for a stock that is not falling, so the
     * note fell back to "" and the player saw a number that never moved.
     *
     * A price is only a price against an income. Showing the rate turns "45 spares" from a fraction
     * of an invisible pile into about a year and a half of the shop's output — which is the same
     * move "months' worth" already makes for food, and it changes no magnitude anywhere.
     */
    fun sparesPerYear(s: WorldState): Double {
        val oreBonus = if (s.hasFlag("ironworks")) 1.0 + 3.0 * s.planet.ore else 1.0
        return modifier(s, "sparesOut") * (0.5 + 0.5 * (s.gauge["competence"]!! / 100.0)) *
                labourIndex(s) * oreBonus * 12.0
    }

    fun sparesCap(s: WorldState): Double =
        max(modifier(s, "sparesStore"), max(1.0, s.popTotal) * 6.0)

    /**
     * Doses a year, from a laboratory if the expedition brought one. Zero if it did not.
     *
     * Same reason as [sparesPerYear]: a stock the player watches has to say where it comes from, or
     * "medicine 42" is a pile of unknown provenance and every price in it is unevaluable.
     */
    fun medicinePerYear(s: WorldState): Double =
        modifier(s, "medOut") * (0.4 + 0.6 * (s.gauge["competence"]!! / 100.0)) * 12.0

    /** Survey data stops paying into skill at this point — `chase` coerces `data/100` at 1.0. */
    const val DATA_USEFUL_TO = 100.0

    fun trend(s: WorldState, idx: Int, now: Double): Double {
        val h = s.yearly
        if (h.size < 3) return 0.0
        val past = h[max(0, h.size - 5)][idx]
        if (past <= 0.001) return 0.0
        val change = (now - past) / past
        return when { change < -0.12 -> -1.0; change > 0.12 -> 1.0; else -> 0.0 }
    }

    /**
     * A snapshot of everything the player can see, so that a change to it can be REPORTED.
     *
     * Two playtests ran into the same wall from opposite directions: one lost fifty people between
     * two screens and was told "- year 54"; the other watched eight capabilities disappear, come
     * back and disappear again with no line of text at all. *"It reads as a bug, not a story beat.
     * The only way to notice is deducing it backward from a LOCKED tag."* The simulation was
     * always right; it just never said anything.
     */
    private fun watched(s: WorldState): Map<String, Double> = (mapOf(
        "pop" to s.popTotal, "deaths" to s.deaths, "cryo" to s.cryo,
        "farm" to s.cultivated, "shelter" to shelterFor(s),
        "power" to (powerGen(s) / max(1.0, powerDraw(s))),
        "water" to s.water, "food" to s.food, "medicine" to s.medicine, "spares" to s.spares,
        "health" to s.gauge["health"]!!
    ) + GAUGES.associate { "g.$it" to s.gauge[it]!! })

    /** Compares the watched quantities to last month's and writes English into [WorldState.changes]. */
    fun noteChanges(s: WorldState) {
        val now = watched(s)
        val caps = capabilities(s)
        val before = s.watchedLast
        if (before != null) {
            // THE LEDGER SAID "N PEOPLE DIED" AND MEANT "THE HEADCOUNT FELL BY N".
            //
            // `died` was a NET population delta, so births masked deaths and any month below the
            // threshold vanished entirely. A reader summing the digest against the roster got 47
            // reported against 7 actual, and then 22 reported against 60 actual -- wrong in both
            // directions inside one run (playtest 06, cross-screen). `s.deaths` has been an
            // accurate cumulative counter the whole time; nothing was reading it.
            val died = now["deaths"]!! - (before["deaths"] ?: 0.0)
            // A toll worth a sentence — and what that is depends on how many people there are.
            // The first version of this rule used `max(3.0, 4%)`, which is a threshold written for
            // a colony four times larger than the one playing: at fifteen people a death went
            // unreported, and losing one of fifteen is enormous. That is the same defect these
            // playtests found in the CONTENT, reproduced in the fix for it.
            if (died >= max(1.0, (before["pop"] ?: 0.0) * 0.04))
                s.changes.add(if (died < 1.5) "somebody died" else "${died.roundToInt()} people died")
            // AND THE OTHER DIRECTION, WHICH HAD NO RULE AT ALL. Waking 57 sleepers at landfall
            // quadrupled the colony between two screens under a digest that read, in full, "year
            // 30" -- the largest demographic event in the game, unreported (playtest 06, D17->D18).
            val woke = (before["cryo"] ?: 0.0) - now["cryo"]!!
            val grew = now["pop"]!! - (before["pop"] ?: 0.0)
            if (woke >= 1.0)
                s.changes.add("${woke.roundToInt()} sleepers are awake — ${now["pop"]!!.roundToInt()} people now, " +
                        "and everything is divided that many ways")
            else if (grew >= max(2.0, (before["pop"] ?: 0.0) * 0.10))
                s.changes.add("there are ${grew.roundToInt()} more of you than there were")
            // A stock falling off a cliff between two screens. One playtest watched medicine go
            // 57 -> 17 under a report that read, in full, "- year 98".
            for ((k, label) in listOf("water" to "water", "food" to "the stores",
                                      "medicine" to "medicine", "spares" to "spares")) {
                val was = before[k] ?: 0.0; val isNow = now[k]!!
                // Rounded the same way the STORES line rounds, so the digest and the panel cannot
                // disagree. "spares fell from 29 to 13" printed above a readout showing 0.0
                // (playtest 05 D69) because the two used different arithmetic on the same number.
                if (was > 20.0 && isNow < was * 0.6)
                    s.changes.add("$label fell from ${was.roundToInt()} to ${isNow.roundToInt()}" +
                            (if (isNow < 0.5) " — there is none left" else ""))
            }
            // THE TANKS AND THE INFIRMARY, CONNECTED. `water 0.0 [empty] LOW` beside "sound health"
            // is arithmetically true — an empty tank is 14 points of health target — and reads as
            // the panel contradicting itself, because nothing ever said the two were related.
            if ((before["water"] ?: 1.0) > 0.01 && now["water"]!! <= 0.01)
                s.changes.add("the tanks are dry — the loop is all that stands between you and thirst, " +
                        "and people are getting sick for it")
            if ((before["health"] ?: 0.0) - now["health"]!! >= 6.0)
                s.changes.add("people are measurably less well than they were")
            // A MOOD THAT CHANGED WORD MUST SAY SO. Iteration 2 watched unity go 4/5 -> 3/5 and
            // standing 4/5 -> 3/5 between two screens with no digest block at all, and skill rise
            // to 5/5 and silently decay back. The ledger watched health only. The player now reads
            // these as a five-rung ladder, so the rung is exactly what has to be reported — and
            // only the rung, so a gauge drifting inside its band stays quiet.
            for (g in GAUGES) {
                val was = before["g.$g"] ?: continue
                val isNow = now["g.$g"] ?: continue
                val bandOf = { v: Double -> ((v / 100.0) * 5).toInt().coerceIn(0, 4) }
                if (bandOf(was) == bandOf(isNow)) continue
                val word = GAUGE_BANDS[g]!![bandOf(isNow)]
                s.changes.add((GAUGE_NAMES[g] ?: g) + " is " + word + " now, not " +
                        GAUGE_BANDS[g]!![bandOf(was)])
            }
            val df = now["farm"]!! - (before["farm"] ?: 0.0)
            if (kotlin.math.abs(df) >= 100.0)
                s.changes.add((if (df > 0) "growing area up " else "growing area down ") +
                        "${kotlin.math.abs(df).roundToInt()} m², now ${now["farm"]!!.roundToInt()}")
            val ds = now["shelter"]!! - (before["shelter"] ?: 0.0)
            if (kotlin.math.abs(ds) >= 1.0)
                s.changes.add("the shelter now holds ${now["shelter"]!!.roundToInt()} people")
            val pb = before["power"] ?: 1.0; val pn = now["power"]!!
            fun band(r: Double) = when { r < 0.6 -> "failing"; r < 0.85 -> "brownouts"; r < 1.2 -> "tight"; else -> "comfortable" }
            if (band(pb) != band(pn)) s.changes.add("power is ${band(pn)} now, not ${band(pb)}")
        }
        s.watchedLast?.let { }
        // Capabilities: say WHY, because the three causes are three different stories — a module
        // broke, the power went, or the colony left the ship behind.
        val lost = s.capsLast.minus(caps)
        val got = caps.minus(s.capsLast)
        if (s.capsLast.isNotEmpty()) {
            for (c in lost) s.changes.add("you can no longer " + Content.capVerb(c) + " — " + capCause(s, c))
            for (c in got) if (!s.granted.contains(c)) s.changes.add("you can " + Content.capVerb(c) + " again")
        }
        s.capsLast = caps
        s.watchedLast = now
    }

    /** Which of the three ways a verb goes away actually happened. */
    private fun capCause(s: WorldState, cap: String): String {
        val sources = s.installed.filter { Content.module(it)?.grants?.contains(cap) == true }
        val broken = sources.filter { (s.moduleHealth[it] ?: 1.0) <= 0.25 }
        if (broken.isNotEmpty()) return (Content.module(broken.first())?.name ?: "a module") + " has failed"
        if (sources.any { (Content.module(it)?.powerDraw ?: 0.0) > 0.5 } && powerRatio(s) < 0.75)
            return "there is not enough power to run it"
        if (sources.any { Content.module(it)?.phase?.let { p -> !p.contains("ANY") && !p.contains(s.phase.name) } == true })
            return "it does not work here"
        return "the colony no longer has what it took"
    }

    fun monthly(s: WorldState, rng: Random) {
        val awake = s.popTotal
        // Power commitments lapse as the work they paid for finishes: a two-year half-life, which
        // is the span the corpus itself names (D008). Without this, a century of funded seasons
        // sums to a brownout nobody chose.
        s.powerCommit *= POWER_COMMIT_DECAY
        if (kotlin.math.abs(s.powerCommit) < 0.05) s.powerCommit = 0.0
        // A propellant plant on the ground makes fuel out of the local air and ice. It is the only
        // way a settled colony acquires manoeuvring reserve, and it is why "there is enough for one
        // ascent" is a claim the player can watch become true on the stores line rather than being
        // told by an event. Capped: the tank is a tank.
        if (s.phase == Phase.SURFACE && s.deltaV < ISRU_TANK_CAP &&
            capabilities(s).contains("ISRU_FUEL") && powerRatio(s) >= 0.85)
            s.deltaV = (s.deltaV + ISRU_KMS_PER_MONTH).coerceAtMost(ISRU_TANK_CAP)
        val pr = powerRatio(s)
        val brownout = pr < 0.85

        // ---- food ----
        // Society's grip on the material world: unhappy, sick, divided or mutinous people do less
        // work, and work is what feeds everyone. Applied as a bounded multiplier so it can hurt
        // badly without becoming a death spiral.
        val li = labourIndex(s)
        val production = s.cultivated * YIELD_PER_M2 * (0.55 + 0.45 * li) *
                (if (brownout) 0.6 else 1.0) *
                (1.0 - 0.6 * s.pressure["ecoStrain"]!!) *
                (1.0 + modifier(s, "yield") + (if (s.hasFlag("bio_industry")) 0.35 else 0.0))
        val consumption = awake * 1.0
        s.food += production - consumption
        // Food does not accumulate forever: you can only store what you built stores for, and the
        // rest spoils. Without this, a good century turns into an invulnerable one.
        val foodCap = modifier(s, "foodStore") * 1.4 + awake * 30.0
        if (s.food > foodCap) s.food = foodCap
        if (s.food < 0) {
            val deficit = -s.food
            s.food = 0.0
            s.gauge["health"] = (s.gauge["health"]!! - 12.0 * (deficit / max(1.0, awake))).coerceIn(0.0, 100.0)
            s.pressure["dissent"] = (s.pressure["dissent"]!! + 0.03).coerceAtMost(1.0)
            s.pressure["inequality"] = (s.pressure["inequality"]!! + 0.01).coerceAtMost(1.0)
            if (deficit > awake * 0.25) kill(s, deficit * 0.15, rng)
        }

        // ---- water loop ----
        // A real ECLSS loop is ~93-98% closed and the shortfall is made up from stores. Over a
        // century that is a slow bleed, so what actually matters is how much the loop DEGRADES:
        // biofilm, scale, a failed polisher. Water shortage is therefore downstream of ecoStrain
        // and entropy — a consequence of neglect rather than an inevitability of arithmetic.
        val recovery = (0.9955 + modifier(s, "waterRecovery")
                - 0.030 * s.pressure["ecoStrain"]!! - 0.020 * s.pressure["entropy"]!!)
                .coerceIn(0.90, 0.9995)
        s.water += awake * (recovery - 1.0) + modifier(s, "waterIn") *
                (if (s.phase == Phase.SURFACE) s.planet.water else 0.0) +
                (if (s.hasFlag("deep_water")) s.planet.water * 4.0 else 0.0)
        // AN EMPTY TANK HAS TO COST SOMETHING. It did not: water clamped at zero and nothing read
        // it, so both playtests spent twenty-odd consecutive screens under
        // `water 0.0 [empty] (falling) LOW` with everyone in "sound health", on a farm that kept
        // growing. One reviewer: *"you cannot fall below empty."* A stock that shouts and never
        // bites teaches the player to stop reading the status line — the opposite of Clarity A3.
        //
        // Priced like the food deficit above, and for the same reason: the loop is 99% closed, so
        // running the reserve to nothing means the 1% is now coming out of people.
        if (s.water < 0) {
            val deficit = -s.water
            s.water = 0.0
            // NOT a health penalty here. Water already reaches health through `watered` in the
            // health target below, and adding a second, non-restoring drain on top of a restoring
            // target cost 15 percentage points of survival across 1,584 runs — a spiral, not a
            // cost. The empty tank is priced once, in `healthTarget`, and made legible in the
            // change ledger. What belongs here is the SOCIAL consequence, which nothing else prices.
            s.pressure["dissent"] = (s.pressure["dissent"]!! + 0.004).coerceAtMost(1.0)
            s.pressure["inequality"] = (s.pressure["inequality"]!! + 0.002).coerceAtMost(1.0)
        }

        // ---- industry: a colony that can make parts is not on a countdown ----
        // An ironworks on an ore-rich world is the difference between rationing parts and making
        // them. This is what "getting the best out of the local environment" has to mean mechanically.
        val oreBonus = if (s.hasFlag("ironworks")) 1.0 + 3.0 * s.planet.ore else 1.0
        val made = modifier(s, "sparesOut") * (if (brownout) 0.3 else 1.0) *
                (0.5 + 0.5 * (s.gauge["competence"]!! / 100.0)) * li * oreBonus
        s.spares += made
        // YOU CAN ONLY STORE WHAT YOU BUILT STORES FOR — the rule food already obeyed, applied to
        // the stocks that were running away with the game. By year 62 both playtests were pricing
        // "years of work" at 0.3-1.5% of the pile: an option cost 30 spares against 1,539 held, and
        // a three-way water fight moved 20 food out of 2,114. The prices were arithmetically
        // correct and materially irrelevant, and five separate readers reached the same sentence —
        // *"the prose and the price disagree, and the price wins."*
        //
        // This is NOT the balance-tuning that `03-PLAYTEST-METHOD.md` §5 says to defer until the
        // corpus is larger. Decisions per century are capped by PACING, not by corpus size, so a
        // century is ~70 decisions whatever the corpus holds; a cost worth 2% of a stock is
        // negligible at every corpus size. The defect is unbounded accumulation, which is physics.
        // The cap is the stores you actually built, or what can be stowed in the corridors —
        // whichever is larger. Stated that way it is a fact about the ship rather than a dial, and
        // it is checkable on the modules screen. Maintenance runs at roughly 20 spares a year, so
        // a depot is about twenty years of margin: enough to be safe, not enough for a 45-spare
        // option to be free.


        // ---- maintenance: spares are consumed, or entropy accelerates ----
        val need = s.installed.size * 0.06 * (1.0 + s.pressure["entropy"]!!)
        val partsQ = if (s.spares >= need) { s.spares -= need; 1.0 }
                     else { val f = (s.spares / need).coerceIn(0.0, 1.0); s.spares = 0.0; f }
        // Maintenance needs hands as well as parts, and a short-staffed colony ages faster.
        // Maintenance needs willing hands as well as parts. This is where a divided colony really
        // pays: the ship ages faster than its spares budget says it should.
        val q = partsQ * li.coerceAtMost(1.0)
        // Even a perfectly maintained ship is measurably older after a century. That is the point.
        s.pressure["entropy"] = (s.pressure["entropy"]!!
                + 0.0030 * (1.0 - modifier(s, "durability").coerceIn(0.0, 0.6))
                - 0.0024 * q).coerceIn(0.0, 1.0)

        // ---- component reliability: complexity x entropy, bought down by pre-flight testing ----
        for (m in s.installed.toList()) {
            val mod = Content.module(m) ?: continue
            val h = s.moduleHealth[m] ?: continue
            if (h <= 0.0) continue
            val p = mod.complexity * 0.00050 * (1.0 + 3.0 * s.pressure["entropy"]!!) * (1.35 - 0.7 * s.build.testing)
            if (rng.nextDouble() < p) {
                val nh = (h - rng.nextDouble(0.3, 0.9)).coerceAtLeast(0.0)
                s.moduleHealth[m] = nh
                s.pressure["entropy"] = (s.pressure["entropy"]!! + 0.03).coerceAtMost(1.0)
                if (h > 0.25 && nh <= 0.25) {
                    // It crossed from working to failed. Can the colony absorb this without
                    // troubling the governor? Spares + hands + know-how = yes, routinely.
                    val cost = 12.0 + 45.0 * mod.complexity
                    val caps = capabilities(s)
                    val canAbsorb = s.spares >= cost &&
                            (caps.contains("FABRICATE") || caps.contains("EXPERTISE")) &&
                            s.gauge["competence"]!! > 30 &&
                            labourIndex(s) > 0.75
                    if (canAbsorb) {
                        s.spares -= cost
                        s.moduleHealth[m] = 0.9
                        s.absorbed++
                        s.log.add("y${s.year}: ${mod.name} failed and was repaired")
                    } else {
                        s.shownFailures++
                        s.pendingIncidents.add("module_failure" to mapOf(
                            "subsystem" to m, "subsystemName" to mod.name,
                            "subsystemDoes" to Setup.describe(mod),
                            "subsystemDoesNow" to Setup.describeS(mod)))
                    }
                } else s.log.add("y${s.year}: ${mod.name} degraded")
            }
        }

        // ---- health is a gauge with a target, not a ratchet ----
        // Radiation dose, low gravity, missing medicine and thin rations all lower the level the
        // colony's health settles at; remove the cause and people recover, slowly.
        val shield = modifier(s, "shielding").coerceIn(0.0, 1.0)
        val gravOk = (if (s.phase == Phase.SURFACE) s.planet.gravity
                      else modifier(s, "spinGravity")).coerceIn(0.0, 1.2)
        val fedNow = (s.food / max(1.0, awake * 4.0)).coerceIn(0.0, 1.0)
        val thirst = awake * WATER_PER_PERSON_MONTH
        val watered = if (s.water > thirst) 1.0 else (s.water / max(1.0, thirst)).coerceIn(0.0, 1.0)
        // Two playtests spent twenty-odd consecutive screens showing `water 0.0 [empty] LOW`
        // beside "sound health" — *"you cannot fall below empty."* The tempting fix was to raise
        // this weight; measured, that cost 11 percentage points of survival and would have been
        // changing BALANCE to fix a COMMUNICATION problem, which is the mistake this project keeps
        // writing down and repeating. An empty tank is already worth 14 points of health target.
        // It is priced correctly and reported nowhere, so the fix is in the change ledger and the
        // readout, not here.
        val healthTarget = 100.0 * (0.20
                + 0.22 * fedNow
                + 0.14 * watered
                + 0.16 * shield
                + 0.16 * gravOk.coerceAtMost(1.0)
                + (if (s.medicine > 0) 0.12 else 0.0)
                - 0.12 * s.pressure["ecoStrain"]!!
                - 0.10 * s.pressure["xenoExposure"]!!).coerceIn(0.0, 1.0)
        s.gauge["health"] = (s.gauge["health"]!! + (healthTarget - s.gauge["health"]!!) * 0.035)
                .coerceIn(0.0, 100.0)
        // A colony with a laboratory can synthesise; one without it runs out and stays out. Before
        // this there was NO source of medicine in the simulation at all, so `medicine > 0` was
        // effectively a 20-year timer rather than a decision.
        val synth = modifier(s, "medOut") * (if (brownout) 0.3 else 1.0) *
                (0.4 + 0.6 * (s.gauge["competence"]!! / 100.0))
        s.medicine = max(0.0, s.medicine + synth - awake * 0.004)

        // ---- ambitions: what the colony is building, when it has anything to spare ----
        for (a in s.ambitions.toList()) {
            val def = AMBITIONS.first { it.id == a.id }
            val monthlySpares = def.sparesPerYear / 12.0
            // Building needs slack. A colony in trouble makes no progress, which is precisely what
            // makes a crisis cost something: it does not merely hurt, it stalls what you were doing.
            val hasSlack = s.spares >= monthlySpares && !brownout && li > 0.85 && s.food > awake * 3
            if (!hasSlack) continue
            s.spares -= monthlySpares
            a.monthsDone++
            if (a.monthsDone >= def.years * 12) {
                s.ambitions.remove(a)
                def.reward.forEach { Expr.apply(s, it, rng, mapOf()) }
                s.pendingAmbition = def
            }
        }

        // ---- demography ----
        aging(s, rng)

        // ---- pressures ----
        pressures(s, rng)

        // ---- gauges chase targets ----
        gauges(s, brownout)

        // Applied here, at the end of the month, so the invariant holds at every month boundary.
        // An event may push a stock above its cap and it settles back next month, which is right:
        // you can pile spares in a corridor for a season, not for a century.
        val awakeNow = max(1.0, s.popTotal)
        val sparesCap = max(modifier(s, "sparesStore"), awakeNow * 6.0)
        if (s.spares > sparesCap) s.spares = sparesCap
        val medCap = max(modifier(s, "medStore"), awakeNow * 1.5)
        if (s.medicine > medCap) s.medicine = medCap
        val waterCap = max(modifier(s, "waterStore"), awakeNow * 8.0)
        if (s.water > waterCap) s.water = waterCap

        // Last, so it compares the month the player will actually be shown.
        noteChanges(s)

        s.month++
    }

    private fun aging(s: WorldState, rng: Random) {
        val toAdult = s.children / (18.0 * 12.0); s.children -= toAdult; s.adults += toAdult
        val toElder = s.adults / (45.0 * 12.0); s.adults -= toElder; s.elders += toElder
        val healthF = s.gauge["health"]!! / 100.0
        val mortality = (0.0004 + 0.0030 * (1.0 - healthF))
        kill(s, (s.elders * mortality * 4 + s.adults * mortality + s.children * mortality * 0.5), rng,
             notable = false)
        // Births happen. People have children because they are people, not because an authority
        // granted permission — the previous model gated births on a `births_permitted` flag, which
        // meant nobody was born during a 15-year crossing unless the player had been asked about it.
        // Policy can only SUPPRESS this, and suppressing it costs something.
        val policy = when {
            s.hasFlag("births_forbidden") -> 0.06     // enforced, resented, and never total
            s.hasFlag("births_rationed") -> 0.55
            else -> 1.0
        }
        val fertile = s.adults * 0.45
        // Willingness, not capability: people have children when they believe there is a future.
        // Calibrated so that a healthy, fed, reasonably happy colony sits just above replacement.
        val want = (0.40 + 0.60 * s.gauge["morale"]!! / 100.0) *
                (0.40 + 0.60 * healthF) *
                (if (s.food > s.popTotal * 6) 1.0 else 0.30)
        // Calibrated against the death rate rather than by feel. Replacement for a healthy colony
        // works out at ~0.0125 here; 0.015 lets a well-run one grow slowly and a struggling one
        // shrink. Both earlier guesses were wrong in opposite directions and each was invisible
        // until measured: 0.020 was a Malthusian blowup (90 -> 169 people in eight years, then
        // famine), 0.0085 was below replacement (42% of colonies died out as "no future").
        val born = fertile * 0.018 * want * policy * (1.0 + modifier(s, "fertility"))
        s.children += born
        s.bornHere += born
        if (born > 0) s.pressure["founderDrift"] = (s.pressure["founderDrift"]!! + born * 0.0009).coerceAtMost(1.0)
        // Forbidding children is not a free lever: it is felt.
        if (s.hasFlag("births_forbidden"))
            s.pressure["dissent"] = (s.pressure["dissent"]!! + 0.0018).coerceAtMost(1.0)
    }

    private fun baseline(s: WorldState, p: String): Double {
        // SIGNED, not max(0, ·). The first version clamped the negative pole away, which made 8 of
        // the 16 authored tags — humane, egalitarian, secular, cautious, earthbound, humanOnly,
        // open, conservationist — mechanically inert, and created a "zero corner": an identity of
        // nothing in particular had the lowest floors of all. Now committing in either direction
        // costs and pays, and being nothing in particular is its own middling outcome.
        fun ax(a: String) = s.axis(a)
        fun band(mid: Double, span: Double, a: String) = (mid + span * ax(a)).coerceAtLeast(0.01)
        return when (p) {
            "inequality"        -> band(0.14, 0.30, "hierarchy")
            "dissent"           -> band(0.13, 0.26, "cruelty")
            "faith"             -> band(0.15, 0.32, "piety")
            "secrecy"           -> band(0.12, 0.28, "secretiveness")
            "aiAutonomy"        -> band(0.10, 0.30, "machineTrust")
            "earthEstrangement" -> band(0.16, 0.32, "independence")
            "entropy"           -> band(0.12, 0.24, "boldness")
            "ecoStrain"         -> band(0.12, 0.24, "expansion")
            "xenoExposure"      -> if (s.phase == Phase.SURFACE) band(0.08, 0.22, "expansion") else 0.0
            else                -> 0.0
        }
    }

    private fun pressures(s: WorldState, rng: Random) {
        fun p(k: String) = s.pressure[k]!!
        /** Relax toward the identity-set baseline, then add this month's drivers. */
        fun relax(k: String, decay: Double, drive: Double) {
            val b = baseline(s, k)
            s.pressure[k] = (b + (p(k) - b) * decay + drive).coerceIn(0.0, 1.0)
        }
        val caps = capabilities(s)
        val moraleF = s.gauge["morale"]!! / 100.0
        val legF = s.gauge["legitimacy"]!! / 100.0

        relax("dissent", 0.994, 0.010 * (1 - moraleF) * (1 - legF) + 0.004 * p("inequality"))
        relax("inequality", 0.9975, 0.0)
        relax("faith", 0.996, 0.0015 * (1 - moraleF) + (if (s.phase == Phase.SPACE) 0.0008 else 0.0))
        relax("secrecy", 0.995, 0.0)
        relax("aiAutonomy", 0.999,
            if (caps.contains("AI_ADVISOR")) 0.0006 * (1 + 2 * (1 - s.gauge["competence"]!! / 100.0)) else 0.0)
        // Was decay 0.9995 against a drive of 0.0004: an equilibrium of drive/(1-decay) = 0.80,
        // reached in EVERY run regardless of play, antenna or identity. A pressure whose value is a
        // constant is not a variable. Faster relaxation makes the equilibrium depend on whether you
        // kept the antenna (~0.13 with, ~0.73 without), which is a decision rather than a fact.
        relax("earthEstrangement", 0.997,
            (if (caps.contains("RADIO_EARTH")) 0.0004 else 0.0022) + (if (s.hasFlag("earth_silent")) 0.004 else 0.0))
        // Every pressure must have a reachable sink, or a build that lacks the relevant module is
        // simply on rails to death — which breaks Principle A5 ("no doom spiral without an off-ramp").
        // Here the free sink is slack: growing area you are not squeezing can be rested and rotated.
        val slack = if (s.cultivated <= 0) 1.0
                    else (s.cultivated * Physics.YIELD_PER_M2 / max(1.0, s.popTotal)).coerceIn(0.0, 2.0)
        relax("ecoStrain", 0.996 - 0.004 * slack,
            if (s.cultivated > 0) 0.0013 * (1.0 - modifier(s, "biodiversity").coerceIn(0.0, 0.9)) else 0.0)
        relax("xenoExposure",
            if (s.phase == Phase.SURFACE) 0.998 else 0.0,
            if (s.phase == Phase.SURFACE)
                0.0020 * (0.3 + s.planet.flora + s.planet.fauna) *
                        (1.0 - modifier(s, "sterilisation").coerceIn(0.0, 0.9)) else 0.0)
        // founderDrift is the one pressure with no baseline and no sink: generations turn over and
        // that is simply true. It is the clock the colony cannot stop. But it was saturating at 0.92
        // in every run by y99, and a saturated pressure is a dead one — slowed so a century lands it
        // around 0.4-0.7 depending on how many generations actually turned over.
        s.pressure["founderDrift"] = (p("founderDrift") + 0.00022).coerceIn(0.0, 1.0)
        // entropy is driven by the maintenance model in monthly(); here it only relaxes to baseline.
        relax("entropy", 1.0, 0.0)
    }

    /**
     * Half-life of about fourteen years. A colony forgets, but not within one governor's attention
     * span — which was the defect: at 6% a month it forgot inside one.
     */
    const val SHIFT_DECAY = 0.9959

    private fun gauges(s: WorldState, brownout: Boolean) {
        // The target is what the PRESSURES imply; the shift is what the DECISIONS bought. A colony
        // whose governor has spent a century earning trust settles somewhere different from one
        // with the same pressures and a different history, and the status line says so.
        for (k in GAUGES) s.gaugeShift[k]?.let { s.gaugeShift[k] = it * SHIFT_DECAY }
        fun chase(k: String, target: Double, rate: Double = 0.06) {
            val cur = s.gauge[k]!!
            val aim = (target + (s.gaugeShift[k] ?: 0.0)).coerceIn(0.0, 100.0)
            s.gauge[k] = (cur + (aim - cur) * rate).coerceIn(0.0, 100.0)
        }
        val fed = (s.food / max(1.0, s.popTotal * 6.0)).coerceIn(0.0, 1.0)
        val healthF = s.gauge["health"]!! / 100.0
        chase("morale", 100 * (0.15 + 0.35 * fed + 0.25 * healthF
                - 0.25 * s.pressure["inequality"]!! - 0.25 * s.pressure["dissent"]!!
                + 0.10 * s.pressure["faith"]!! - (if (brownout) 0.15 else 0.0)).coerceIn(0.0, 1.0))
        chase("legitimacy", 100 * (0.55 - 0.45 * s.pressure["dissent"]!! - 0.20 * s.pressure["secrecy"]!!
                - 0.25 * s.pressure["aiAutonomy"]!! + 0.15 * (s.gauge["morale"]!! / 100.0)).coerceIn(0.0, 1.0))
        chase("cohesion", 100 * (0.65 - 0.30 * s.pressure["founderDrift"]!! - 0.30 * s.pressure["inequality"]!!
                - 0.20 * s.pressure["dissent"]!! + 0.15 * s.pressure["faith"]!!).coerceIn(0.0, 1.0))
        chase("competence", 100 * (0.35 + 0.35 * (if (Physics.capabilities(s).contains("ARCHIVE")) 1.0 else 0.4)
                + 0.20 * (s.data / 100.0).coerceAtMost(1.0)
                - 0.25 * s.pressure["founderDrift"]!!).coerceIn(0.0, 1.0), 0.03)
    }
}

// =====================================================================================
//  DIRECTOR — sources 1, 2, 3 of D001, in priority order.
// =====================================================================================
class Director(val rng: Random) {

    // Telegraph rate and decision rate are SEPARATE dials. Conflating them was a mistake: the
    // symptoms that make hidden state legible (Principle A3) need to be frequent, while decisions
    // need to be rare enough that each one is an event in the player's memory. One knob cannot
    // serve both, and tuning the shared knob always broke one of them.
    // Calibrated against observed tension (~4-6 with several pressures mid-range) to land near one
    // decision per year, which is the designer's stated target pacing.
    var K_DECISION = 0.022        // P(a decision this month) = 1 - exp(-K * tension)
    var K_SYMPTOM  = 0.050        // P(a symptom report this month)
    var MIN_DECISION_GAP = 6      // months; crises and scheduled consequences ignore this
    var BURST_GAP = 0             // months, while the colony is in a burst window
    var BURST_MONTHS_AFTER_LANDFALL = 30
    /**
     * The drought rail. Every long-run system examined in docs/research/R1 ships a separate, dumber
     * guarantee that *something* happens if too long has passed (Vermintide's max_delay_until_next_horde,
     * CK3's `fallback`, Arkham's agenda deck) — because a probabilistic pacing curve will, on some
     * seeds, simply go quiet for a decade. Tension alone is not enough.
     */
    var MAX_DECISION_GAP = 54     // months
    var K_OPPORTUNITY = 0.038     // P(an opportunity) rises as tension FALLS
    var OPP_CEILING = 2.8         // tension above which the colony has no spare attention
    /**
     * DON'T SPEND THE PLAYER'S ATTENTION ON THE ONLY CARD YOU HAVE.
     *
     * The opportunity rate is a function of LOW tension, which is the early game's permanent
     * condition: ~9% a month at launch. That is fine against a full deck and wrong against a thin
     * one — after the premise fixes of §1g, exactly one opportunity was eligible before year 6, and
     * it became the opening event of 74% of runs. Every game started with the same biologist asking
     * for the same instrument.
     *
     * So the rate now scales with how many opportunities the colony can actually reach: a pool of
     * one fires at a third of the rate of a pool of three. A thin early game becomes QUIET, which
     * is a legitimate outcome (D001 C7), instead of repetitive, which is not. Set to 1 to ablate.
     */
    var OPP_POOL_FULL = 3
    // Ablation switches. docs/research/R1's closest published relative (King of Dragon Pass) uses
    // NO scoring at all — boolean eligibility, uniform draw, a multi-year lockout — so each of these
    // terms has to justify itself against that baseline rather than being assumed.
    var USE_NOVELTY = true
    var USE_SPOTLIGHT = true
    var DEMAND_EXP = 1.6
    var SPOT_MAX = 0.8
    var SPOT_N = 2
    var STALE_W = 0.5
    /**
     * WHY THIS EVENT, NOW — the selection diagnosis, for the designer view (`d` in game).
     *
     * The static metadata already shown there (source, weight, cooldown) says what an event IS. It
     * does not say why it arrived THIS MONTH, which is the only question a playtester has when an
     * event's fiction does not match the colony in front of them: is this arrival earned, or is the
     * deck handing out a grievance the colony has not got? Answering it by reading Sim.kt is how a
     * year-0 petition survives 1,700 harness runs and gets found by a human in five minutes.
     */
    var lastWhy: List<String> = emptyList()
        private set

    private fun fmt(v: Double) = if (v >= 100) Fmt.int(v) else Fmt.dec(v, 2)

    /** `pressure.dissent >= 0.35` -> `pressure.dissent >= 0.35 (now 0.41)`. */
    private fun withValue(s: WorldState, term: String): String {
        val t = term.trim()
        val path = Regex("""^!?([A-Za-z_][A-Za-z0-9_.]*)\s*(?:<=|>=|==|!=|<|>)""")
            .find(t)?.groupValues?.get(1) ?: return t
        return Expr.value(s, path)?.let { "$t (now ${fmt(it)})" } ?: t
    }

    private fun gateLine(s: WorldState, e: GameEvent): String =
        if (e.requires.isEmpty()) "gates: none — this event can fire the month the colony launches"
        else "gates: " + e.requires.joinToString("  ·  ") { r ->
            r.split('|').joinToString(" | ") { withValue(s, it) }
        }

    var lastDeckSize = 0
    var lastTension = 0.0
    var wantedEvent = 0
    var starvedDeck = 0
    var railFired = 0
    val deckSizes = mutableListOf<Int>()

    fun demand(s: WorldState, p: String): Double {
        val lvl = s.pressure[p]!!
        if (lvl < 0.12) return 0.0
        val stale = ((s.month - (s.pressureLastExpressed[p] ?: -60)) / 60.0).coerceIn(0.0, 1.0)
        return lvl.pow(DEMAND_EXP) * (1.0 + STALE_W * stale)
    }

    /**
     * The sum of the THREE most demanding pressures, not all ten.
     *
     * Summing ten accumulating pressures made pacing a function of how many things were wrong at
     * once, so decisions per decade ramped 4.0 -> 11.4 across a run while the state itself went flat
     * after y80 — a late game that was busier and less meaningful at the same time. Top-3 bounds
     * tension at ~4.5 instead of ~15 and makes it mean "how bad is the worst of it", which is also
     * what a governor actually experiences.
     */
    /**
     * How long the colony gets between questions right now.
     *
     * A fixed six-month floor produced an unnatural silence at exactly the moment a real colony is
     * busiest: you land, and then nothing happens for half a year. A settlement in its first years,
     * or one whose stocks are failing, should be able to ask you two things in a row.
     */
    fun decisionGap(s: WorldState): Int {
        val settling = s.landfallMonth >= 0 &&
                s.month - s.landfallMonth in 0..BURST_MONTHS_AFTER_LANDFALL
        val pressed = Physics.powerRatio(s) < 0.9 ||
                s.food < s.popTotal * 6 ||
                s.water < s.popTotal * 2 ||
                tension(s) > 4.0
        return when {
            settling -> BURST_GAP
            pressed -> 2
            else -> MIN_DECISION_GAP
        }
    }

    fun tension(s: WorldState): Double =
        PRESSURES.map { demand(s, it) }.sortedDescending().take(3).sum()

    /** Draw a theme by demand, then a card from that theme's deck. Returns null if no deck has cards. */
    private fun drawFromDecks(s: WorldState, caps: Set<String>, wantSymptom: Boolean,
                              ignoreDemandFloor: Boolean = false): Pair<GameEvent, String>? {
        val d = PRESSURES.associateWith {
            if (ignoreDemandFloor) maxOf(demand(s, it), 0.001) else demand(s, it)
        }.filterValues { it > 0.0 }.toMutableMap()
        while (d.isNotEmpty()) {
            val t = weighted(d, rng) ?: break
            val cand = Content.byPressure(t).filter {
                eligible(s, caps, it) && (it.kindE == EventKind.SYMPTOM) == wantSymptom
            }
            if (cand.isEmpty()) { d.remove(t); continue }
            if (!wantSymptom) { deckSizes.add(cand.size); if (cand.size < 5) starvedDeck++ }
            val scores = cand.associateWith {
                it.weight *
                        (if (USE_NOVELTY) novelty(s, it) else 1.0) *
                        (if (USE_SPOTLIGHT) spotlight(s, caps, it) else 1.0)
            }
            val e = weighted(scores, rng) ?: run { d.remove(t); null } ?: continue
            s.pressureLastExpressed[t] = s.month
            val warned = (s.symptomWarnings[t] ?: listOf<Int>()).count { s.month - it in 0..120 }
            lastWhy = listOf(
                (if (wantSymptom) "channel: SYMPTOM report — " else "channel: DECISION — ") +
                        "the '$t' deck won the theme lottery" +
                        (if (ignoreDemandFloor) "   [DROUGHT RAIL: ${MAX_DECISION_GAP}mo of silence, floors ignored]" else ""),
                "$t: level ${fmt(s.pressure[t]!!)} -> demand ${fmt(demand(s, t))} of ${fmt(d.values.sum())} " +
                        "across ${d.size} live deck(s);  symptoms of it in the last decade: $warned",
                "deck: ${cand.size} eligible of ${Content.byPressure(t).count { (it.kindE == EventKind.SYMPTOM) == wantSymptom }} " +
                        "tagged '$t';  this one held ${fmt(100.0 * scores[e]!! / scores.values.sum())}% of the weight " +
                        "(seen ${s.fireCount[e.id] ?: 0}x before)",
                gateLine(s, e),
                "pacing: tension ${fmt(tension(s))};  ${s.month - s.lastDecisionMonth} months since the " +
                        "last decision (floor ${decisionGap(s)})"
            )
            return e to t
        }
        return null
    }

    /**
     * THE TWO-WARNINGS INTERLOCK.
     *
     * A3's hard rule says no hidden parameter may cause an irreversible event without two prior
     * player-visible signals. Measured as a *hope* it sat at 65% on an honest 10-year window, and no
     * amount of tuning would fix it: it needed two draws from a 10-way theme lottery over 3 symptoms
     * on long cooldowns, while anti-repetition demanded the opposite. So it stops being a hope and
     * becomes a precondition — a crisis that names a hidden cause simply cannot fire until that
     * cause has been visible twice in the last decade. The pressure keeps rising in the meantime,
     * which makes the eventual crisis worse, not absent.
     */
    private fun eligible(s: WorldState, caps: Set<String>, e: GameEvent): Boolean {
        if (!e.phase.contains("ANY") && !e.phase.contains(s.phase.name)) return false
        // THE INTERLOCK, generalised. Originally it guarded crises only. But any event that
        // *requires* a tension to be high is claiming the colony has been building toward this —
        // a splinter group forming, a faction hardening — and such an event arriving cold reads as
        // the game inventing a conflict rather than reporting one. If an event names a pressure as
        // its precondition, that pressure must already have shown itself twice.
        // ...but NOT to symptoms. Symptoms are themselves the warnings, and they are gated on the
        // very pressures they announce, so applying the interlock to them deadlocks the whole
        // corpus: nothing may warn until something has warned twice. That mistake took coverage
        // from 100% to 57% in one line, which is a good argument for running the sweep after every
        // change to eligibility.
        val gatingPressures = if (e.kindE == EventKind.SYMPTOM) emptyList() else e.requires.mapNotNull { r ->
            Regex("""^pressure\.([A-Za-z]+)\s*>=\s*([0-9.]+)$""").find(r.trim())?.let {
                if (it.groupValues[2].toDouble() >= 0.30) it.groupValues[1] else null
            }
        }
        if (gatingPressures.isNotEmpty() && gatingPressures.none { warnedTwice(s, it) }) return false
        if (e.kindE == EventKind.CRISIS && e.pressures.isNotEmpty() &&
            e.pressures.keys.none { warnedTwice(s, it) }) return false
        if (e.once && (s.fireCount[e.id] ?: 0) > 0) return false
        val last = s.lastFired[e.id]
        if (last != null && s.month - last < e.cooldownYears * 12) return false
        return Expr.all(s, caps, e.requires)
    }

    fun offeredOptions(s: WorldState, caps: Set<String>, e: GameEvent): List<Option> =
        e.options.filter { Expr.all(s, caps, it.requires) }

    private fun spotlight(s: WorldState, caps: Set<String>, e: GameEvent): Double {
        var b = 1.0
        for (o in e.options) {
            for (r in o.requires.flatMap { it.split('|') }.map { it.trim() }) {
                if (!r.startsWith("cap:")) continue
                val c = r.removePrefix("cap:")
                if (!caps.contains(c)) continue
                val used = s.capOffered[c] ?: 0
                if (used < SPOT_N) b += SPOT_MAX * (1.0 - used.toDouble() / SPOT_N)
            }
        }
        return b
    }

    private fun novelty(s: WorldState, e: GameEvent): Double {
        val n = s.fireCount[e.id] ?: 0
        return 1.0 / (1.0 + 0.9 * n)
    }

    var incidentBindings: Map<String, String> = mapOf()
    var currentThread: Thread? = null

    /** Returns the event to present this month, or null to keep the clock running. */
    fun pick(s: WorldState): Pair<GameEvent, String>? {
        incidentBindings = mapOf()
        currentThread = null
        val caps = Physics.capabilities(s)

        // ---- source 1: SCHEDULED — a thread comes due. This is the memory mechanism. ----
        // A due thread used to fire its event unconditionally: no `once` check, no cooldown, no
        // phase check. That let a one-time event repeat whenever two threads named it — which is
        // exactly what happened when a setup bug scheduled the Earth-goes-quiet arc twice. The
        // memory mechanism must still obey the rules that govern every other source.
        while (true) {
            val due = s.threads.firstOrNull { it.dueMonth in 0..s.month } ?: break
            s.threads.remove(due)
            // PLUGGABLE STAGES. The thread names a beat; any event declaring `stageOf` that beat
            // can stand in for it. Candidates are filtered by their own gates, so which middle a
            // chain plays is decided by what the colony IS and what it DID — the same opening
            // leading somewhere different, which is a lot of variety for very little content.
            val stand = Content.standIns(due.familyId).filter { alt ->
                alt.requires.all { Expr.cond(s, caps, it) } &&
                (alt.phase.contains("ANY") || alt.phase.contains(s.phase.name)) &&
                !(alt.once && (s.fireCount[alt.id] ?: 0) > 0) &&
                (s.lastFired[alt.id]?.let { s.month - it >= alt.cooldownYears * 12 } ?: true)
            }
            val base = Content.event(due.familyId)
            val ev = (if (stand.isEmpty()) base
                      else weighted(stand.associateWith { it.weight * novelty(s, it) }, rng) ?: base)
                ?: continue
            if (ev.once && (s.fireCount[ev.id] ?: 0) > 0) continue          // already happened
            val last = s.lastFired[ev.id]
            if (last != null && s.month - last < ev.cooldownYears * 12) continue
            if (!ev.phase.contains("ANY") && !ev.phase.contains(s.phase.name)) {
                due.dueMonth = s.month + 12                                  // wrong phase: wait a year
                s.threads.add(due)
                continue
            }
            currentThread = due
            incidentBindings = due.bindings
            lastWhy = listOf(
                "channel: SCHEDULED — a consequence set earlier came due; this is the memory mechanism",
                "thread '${due.familyId}' was due month ${due.dueMonth} (year ${due.dueMonth / 12}); it is now month ${s.month}",
                gateLine(s, ev)
            )
            return ev to "scheduled"
        }

        // ---- source 2a: incidents the simulation just emitted ----
        if (s.pendingIncidents.isNotEmpty()) {
            val inc = s.pendingIncidents.removeAt(0)
            Content.event(inc.first)?.let {
                incidentBindings = inc.second
                lastWhy = listOf(
                    "channel: EMITTED — the physics did this, this month, and the event reports it",
                    "no deck, no lottery, no pressure: it is arithmetic that already happened" +
                            (if (s.pendingIncidents.isEmpty()) "" else "   (${s.pendingIncidents.size} more queued)")
                )
                return it to "sim"
            }
        }

        // ---- source 2b: sustained physical conditions that keep asserting themselves ----
        // For SIM events, `weight` is a MONTHLY PROBABILITY once the physical trigger holds.
        // weight 1.0 = "this happens now, inevitably" (you ran out of food);
        // weight 0.004 = "this becomes possible" (a hull that has not been maintained).
        val simPool = Content.bySource("SIM").filter { eligible(s, caps, it) && rng.nextDouble() < it.weight }
        if (simPool.isNotEmpty()) {
            val e = simPool.maxByOrNull { it.weight }!!
            lastWhy = listOf(
                "channel: SIM — a physical condition holds, and it is re-rolled every month",
                "monthly chance once the condition holds: ${fmt(e.weight)}" +
                        (if (simPool.size > 1) "   (${simPool.size} conditions fired; the heaviest wins)" else ""),
                gateLine(s, e)
            )
            return e to "sim"
        }

        // ---- source 3: PRESSURE. Two independent rolls (see K_DECISION / K_SYMPTOM). ----
        val t = tension(s)
        lastTension = t
        val droughted = s.month - s.lastDecisionMonth >= MAX_DECISION_GAP
        if (droughted) railFired++
        if (s.month - s.lastDecisionMonth >= decisionGap(s) &&
            (droughted || rng.nextDouble() < 1.0 - exp(-K_DECISION * t))) {
            wantedEvent++
            drawFromDecks(s, caps, wantSymptom = false, ignoreDemandFloor = droughted)?.let { return it }
        }
        // Calm buys ambition, not silence. Without this, the best-played colony got the fewest
        // decisions, because tension was simultaneously the thing to suppress and the pacing dial.
        if (t < OPP_CEILING && s.month - s.lastDecisionMonth >= MIN_DECISION_GAP) {
            if (rng.nextDouble() < 1.0 - exp(-K_OPPORTUNITY * (OPP_CEILING - t))) {
                val pool = Content.byKind("OPPORTUNITY").filter { eligible(s, caps, it) }
                val thin = (pool.size.toDouble() / OPP_POOL_FULL).coerceAtMost(1.0)
                if (pool.isNotEmpty() && rng.nextDouble() < thin) {
                    val scores = pool.associateWith {
                        it.weight * (if (USE_NOVELTY) novelty(s, it) else 1.0) *
                                (if (USE_SPOTLIGHT) spotlight(s, caps, it) else 1.0)
                    }
                    weighted(scores, rng)?.let {
                        lastWhy = listOf(
                            "channel: OPPORTUNITY — drawn because tension is LOW, not because anything is wrong",
                            "tension ${fmt(t)} is under the ceiling ${fmt(OPP_CEILING)};  ${pool.size} opportunities " +
                                    "were affordable to this colony",
                            gateLine(s, it)
                        )
                        return it to "opportunity"
                    }
                }
            }
        }
        if (rng.nextDouble() < 1.0 - exp(-K_SYMPTOM * t))
            drawFromDecks(s, caps, wantSymptom = true)?.let { return it }
        return null
    }

    companion object {
        /** Has this pressure produced two visible symptoms within the last decade? */
        fun warnedTwice(s: WorldState, pressure: String): Boolean =
            (s.symptomWarnings[pressure] ?: listOf<Int>()).count { s.month - it in 0..120 } >= 2

        fun <T> weighted(m: Map<T, Double>, rng: Random): T? {
            val tot = m.values.sum()
            if (tot <= 0.0) return null
            var r = rng.nextDouble() * tot
            for ((k, v) in m) { r -= v; if (r <= 0) return k }
            return m.keys.lastOrNull()
        }
    }
}

// =====================================================================================
//  ENGINE — the turn loop. The clock runs in months; it STOPS on an event, and always
//  stops at the year boundary so the player gets an annual report even in quiet times.
//  ("Nothing happened" is a legitimate outcome — D001 C7 — but it arrives on a heartbeat.)
// =====================================================================================
/**
 * Lets a UI observe the loop without reimplementing it. The headless harness ignores all of this;
 * the text game implements it. Keeping one loop matters: the thing the player experiences must be
 * the same thing the 1,700-run sweep measures, or the measurements stop meaning anything.
 */
interface EngineListener {
    /** Fired before the player is asked. `all` includes unavailable options so a UI can show them greyed. */
    fun onPresent(s: WorldState, e: GameEvent, title: String, body: String,
                  all: List<Option>, offered: List<Option>, caps: Set<String>) {}
    fun onOutcome(text: String, effects: List<String> = emptyList()) {}
    fun onSymptom(title: String, body: String) {}
    /** Fired after an event has been fully applied (choice, outcome, effects). */
    fun onResolved() {}
    fun onYearEnd(s: WorldState, decisionsThisYear: Int) {}
    fun onEnd(s: WorldState, reason: String) {}
}

class Engine(val s: WorldState, val bot: Bot, val rng: Random, val maxYears: Int = 100) {
    var listener: EngineListener? = null
    val dir = Director(rng)
    var stopsWithoutDecision = 0
    var stopsWithDecision = 0
    val gaps = mutableListOf<Int>()
    var lastEventMonth = 0
    val crisisCauses = mutableListOf<Pair<String, Boolean>>()        // eventId, hadTwoWarnings

    var decisionsThisYear = 0
    val decisionsPerYear = mutableListOf<Int>()

    /**
     * The clock runs in MONTHS and stops when something needs the player. It always stops at the
     * year boundary as well, so a quiet century still has a heartbeat — an annual report the player
     * reads and clicks past. This is what lets "nothing happened" read as relief rather than as an
     * empty turn, and it is what makes a post-landfall burst possible: nothing caps decisions at
     * one per year, the tension does.
     */
    fun run() {
        Setup.begin(s, rng)
        while (s.alive && s.year < maxYears) {
            Physics.monthly(s, rng)
            checkEnd()
            if (!s.alive) break
            Setup.maybeLandfall(s, rng)

            // Up to three questions can land in the same month while the colony is settling or in
            // trouble. "Ask one thing, then immediately another" is what a founding year feels like;
            // one-per-month made the busiest period of the game the quietest.
            val burst = dir.decisionGap(s) == 0
            var asked = 0
            while (true) {
                val picked = dir.pick(s) ?: break
                // A symptom carries no decision: it goes into the annual report and the clock keeps
                // running. Only a real decision takes the player's turn.
                if (resolve(picked.first, picked.second)) { decisionsThisYear++; asked++ }
                if (!burst || asked >= 3 || !s.alive) break
            }
            if (s.month % 12 == 0) {                       // annual report
                // Index 5 is medicine, added so every stock the interface shows can be expressed
                // as time-to-depletion. Indices 0-4 are unchanged: the harness reads them.
                s.yearly.add(doubleArrayOf(s.food, s.water, s.spares, s.popTotal,
                    Physics.powerRatio(s), s.medicine))
                if (s.yearly.size > 8) s.yearly.removeAt(0)
                s.history.add(Triple(s.year, s.popTotal, s.food))
                decisionsPerYear.add(decisionsThisYear)
                if (decisionsThisYear == 0) stopsWithoutDecision++ else stopsWithDecision++
                listener?.onYearEnd(s, decisionsThisYear)
                decisionsThisYear = 0
            }
        }
        if (s.alive && s.endReason.isEmpty()) s.endReason = "survived"
        listener?.onEnd(s, s.endReason)
    }

    /**
     * "Survived" used to mean `popTotal >= 2` — so an 86% survival rate was really a "still has two
     * people" rate, and a colony running at 17% of its power demand for a century with 8 of its 24
     * founders left counted as a win while serving fifty more decisions. That blinded every balance
     * metric in the harness and violated A5's own "no doom spiral without an off-ramp".
     *
     * A colony is lost when it can no longer be a colony.
     */
    private fun checkEnd() {
        if (s.popTotal < 2.0) { s.alive = false; s.endReason = "extinction"; return }
        s.peakPop = maxOf(s.peakPop, s.popTotal)

        // No future: too few people, nobody being born, nobody in storage.
        val noFuture = s.popTotal < 8.0 && s.children < 0.5 && s.cryo < 1.0 && s.year > 15
        s.noFutureMonths = if (noFuture) s.noFutureMonths + 1 else 0
        if (s.noFutureMonths >= 180) { s.alive = false; s.endReason = "no future"; return }

        // The lights went out: sustained inability to meet the power demand of staying alive.
        val dark = Physics.powerRatio(s) < 0.45
        s.lowPowerMonths = if (dark) s.lowPowerMonths + 1 else 0
        if (s.lowPowerMonths >= 36) { s.alive = false; s.endReason = "systems failure"; return }

        // Starving with no way to grow out of it.
        val starving = s.food < 1.0 &&
                s.cultivated * Physics.YIELD_PER_M2 < s.popTotal * 0.6
        s.starvingMonths = if (starving) s.starvingMonths + 1 else 0
        if (s.starvingMonths >= 30) { s.alive = false; s.endReason = "starvation"; return }

        // Collapse: a fraction of what it was, and not recovering.
        if (s.peakPop >= 20 && s.popTotal < s.peakPop * 0.2 && s.gauge["health"]!! < 30) {
            s.alive = false; s.endReason = "collapse"
        }
    }

    fun resolve(e: GameEvent, theme: String): Boolean {
        val caps = Physics.capabilities(s)
        val bindings = if (dir.incidentBindings.isNotEmpty()) dir.incidentBindings else Setup.bind(s, e, rng)
        val offered = dir.offeredOptions(s, caps, e)
        // Alternation-aware, like the validator: an option gated on "cap:A | cap:B" exercises
        // whichever of A or B the player actually has.
        val gated = offered
            .flatMap { o -> o.requires.flatMap { it.split('|') } }
            .map { it.trim() }
            .filter { it.startsWith("cap:") }
            .map { it.removePrefix("cap:") }
            .filter { caps.contains(it) }
        gated.forEach { s.capOffered[it] = (s.capOffered[it] ?: 0) + 1 }

        // Cycle the wording for events that recur. Index 0 is the base text.
        val seen = s.fireCount[e.id] ?: 0
        val variant = if (e.variants.isEmpty()) null else e.variants[(seen - 1).coerceAtLeast(0) % e.variants.size]
        var shownTitle = (if (seen > 0 && variant != null && variant.title.isNotEmpty()) variant.title else e.title)
        for ((k, v) in bindings) shownTitle = shownTitle.replace("{$k}", v)
        gaps.add(s.month - lastEventMonth); lastEventMonth = s.month
        if (e.options.isNotEmpty()) s.lastDecisionMonth = s.month
        s.fireCount[e.id] = (s.fireCount[e.id] ?: 0) + 1
        s.lastFired[e.id] = s.month

        if (e.kindE == EventKind.SYMPTOM && e.symptomOf.isNotEmpty())
            s.symptomWarnings.getOrPut(e.symptomOf) { mutableListOf() }.add(s.month)
        // The two-warnings rule (A3) governs HIDDEN causes only. A crisis whose cause is a visible
        // number the player was watching (the food ran out) needs no telegraphing — the telegraph
        // was the number. So only crises that name a pressure are held to the rule.
        if (e.kindE == EventKind.CRISIS && e.pressures.isNotEmpty()) {
            val causes = e.pressures.keys
            // 50 years was a meaningless window: almost any symptom ever emitted counted as a
            // warning for a crisis half a century later. 10 years is a span a player can actually
            // hold in mind, which is what the rule is FOR.
            val warned = causes.any { c -> Director.warnedTwice(s, c) }
            crisisCauses.add(e.id to warned)
        }

        var shownBody = if (seen > 0 && variant != null) variant.body else e.body
        for ((k, v) in bindings) shownBody = shownBody.replace("{$k}", v)
        if (e.kindE == EventKind.SYMPTOM) listener?.onSymptom(shownTitle, shownBody)
        else listener?.onPresent(s, e, shownTitle, shownBody, e.options, offered, caps)

        val chosen = if (offered.isEmpty()) null else bot.choose(s, e, offered, rng)
        if (chosen != null) {
            for (r in chosen.requires.flatMap { it.split('|') }.map { it.trim() })
                if (r.startsWith("cap:")) {
                    val c = r.removePrefix("cap:")
                    if (caps.contains(c)) s.capUsed[c] = (s.capUsed[c] ?: 0) + 1
                }
            for (t in chosen.tags) s.tagLedger[t] = (s.tagLedger[t] ?: 0.0) + 1.0
            val th = dir.currentThread
            for (eff in chosen.effects) Expr.apply(s, eff, rng, bindings, th)
            if (chosen.outcomes.isNotEmpty()) {
                // OUTCOMES READ THE BUILD (F9). A branch whose own gates fail is not in the draw,
                // and the remainder renormalise — so the same option resolves differently depending
                // on what the colony is and what it has done. This is the half of build-coupling
                // that was missing: every other layer acts on the MENU, and the player takes an
                // ungated option 78% of the time.
                //
                // An UNGATED branch is the fallback, not a member of the draw, whenever any gated
                // branch applies. Every option mixing the two is written that way ("with EVA it goes
                // like this, otherwise like that"), and the validator's own advice for
                // `[no-default-outcome]` is "leave one branch ungated". Drawing them together made
                // a probe survey a silent coin-flip against the no-instruments branch, and an EVA
                // volunteer a four-way draw across two sets of odds that each summed to one.
                val live = Physics.liveOutcomes(s, chosen, caps)
                s.gamblesResolved++
                if (live.size < chosen.outcomes.size) s.gamblesBuildDetermined++
                val o = Director.weighted(live.associateWith { it.p }, rng)
                o?.effects?.forEach { Expr.apply(s, it, rng, bindings, th) }
                if (o != null && o.text.isNotBlank()) listener?.onOutcome(o.text, o.effects)
            }
        }

        s.trace.add(TraceRow(s.month, e.id, shownTitle, e.kind, theme, offered.size, gated.distinct(),
            chosen?.label ?: "-", chosen?.tags ?: listOf(), dir.lastDeckSize, s.pressure.toMap()))
        checkEnd()
        listener?.onResolved()
        return e.options.isNotEmpty() && offered.isNotEmpty()
    }
}
